import React, { useEffect } from 'react';
import usePageTransition from '../hooks/usePageTransition';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram } from 'react-icons/fa';
import Section from '../components/common/Section';
import Button from '../components/common/Button';

const PageHeader = styled.div`
  background: ${({ theme }) => theme.colors.gradient.primary};
  padding: ${({ theme }) => `${theme.space['3xl']} 0`};
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.gradient.glow};
    opacity: 0.3;
    z-index: 1;
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
`;

const PageTitle = styled(motion.h1)`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: 3.5rem;
  background: linear-gradient(135deg, #fff 0%, ${({ theme }) => theme.colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const PageDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  line-height: 1.6;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xl};
  max-width: 1200px;
  margin: 0 auto;
  align-items: start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 2;
  }
`;

const ContactForm = styled(motion.div)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 1;
  }
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const InfoCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: ${({ theme }) => theme.space.xl};
  border-left: 4px solid ${({ theme }) => theme.colors.accent};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${({ theme }) => theme.colors.gradient.accent};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-5px);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const InfoItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.md};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.blob};
  background: ${({ theme }) => theme.colors.gradient.neon};
  color: ${({ theme }) => theme.colors.dark};
  flex-shrink: 0;
  box-shadow: ${({ theme }) => theme.shadows.neon};
  animation: ${({ theme }) => theme.animations.pulse};
`;

const InfoContent = styled.div`
  h4 {
    margin-bottom: ${({ theme }) => theme.space.xs};
  }
  
  p {
    color: ${({ theme }) => theme.colors.grey[700]};
    margin-bottom: 0;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space.lg};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme }) => theme.colors.grey[200]};
  color: ${({ theme }) => theme.colors.dark};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.gradient.neon};
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  svg {
    position: relative;
    z-index: 2;
    transition: all 0.3s ease;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.dark};
    transform: translateY(-3px) rotate(8deg);
    box-shadow: ${({ theme }) => theme.shadows.neon};
    
    &::before {
      opacity: 1;
    }
  }
`;

const FormCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-left: 4px solid ${({ theme }) => theme.colors.secondary};
  transition: all ${({ theme }) => theme.transitions.normal};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.fonts.body};
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme }) => theme.colors.grey[100]};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadows.neon};
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.fonts.body};
  resize: vertical;
  min-height: 150px;
  transition: all ${({ theme }) => theme.transitions.normal};
  background-color: ${({ theme }) => theme.colors.grey[100]};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadows.neon};
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MapContainer = styled.div`
  margin-top: ${({ theme }) => theme.space.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  height: 400px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const Contact: React.FC = () => {
  // Use our custom hook for optimized page transitions
  const { isReturnVisit, isPageReady } = usePageTransition();
  const prefersReducedMotion = useReducedMotion();
  
  // Optimize animations based on user preferences and return visits
  const animationSettings = {
    // If user prefers reduced motion or is a return visitor, use simpler animations
    initial: prefersReducedMotion || isReturnVisit ? { opacity: 0 } : { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      type: 'spring', 
      stiffness: prefersReducedMotion || isReturnVisit ? 100 : 50, 
      damping: prefersReducedMotion || isReturnVisit ? 20 : 10,
      duration: prefersReducedMotion || isReturnVisit ? 0.2 : undefined
    }
  };
  
  // Preload map iframe
  useEffect(() => {
    // Add a small timeout to delay loading the map until after critical content
    const timer = setTimeout(() => {
      const mapIframe = document.querySelector('iframe');
      if (mapIframe) {
        mapIframe.setAttribute('loading', 'lazy');
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("This is a static form. Feature coming soon!");
  };
  
  // Don't render until the page is ready
  if (!isPageReady) {
    return <div style={{ height: '100vh' }} />; // Empty placeholder while loading
  }
  
  return (
    <>
      <PageHeader>
        <div className="container">
          <PageTitle
            initial={animationSettings.initial}
            animate={animationSettings.animate}
            transition={animationSettings.transition}
          >
            Contact Us
          </PageTitle>
          <PageDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.2 }}
          >
            Get in touch with our team for any inquiries or to discuss your digital transformation needs
          </PageDescription>
        </div>
      </PageHeader>
      
      <Section id="contact">
        <ContactContainer>
          <ContactInfo
            initial={{ opacity: 0, x: isReturnVisit ? -20 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ 
              type: 'spring', 
              stiffness: isReturnVisit ? 150 : 100, 
              damping: isReturnVisit ? 20 : 15, 
              delay: isReturnVisit ? 0 : 0.1 
            }}
          >
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15 }}
            >
              Get In Touch
            </SectionTitle>
            
            <InfoCard>
              <InfoItems>
                <InfoItem>
                  <IconWrapper>
                    <FaMapMarkerAlt />
                  </IconWrapper>
                  <InfoContent>
                    <h4>Our Location</h4>
                    <p>Saguna More, Patna, Bihar</p>
                  </InfoContent>
                </InfoItem>
                
                <InfoItem>
                  <IconWrapper>
                    <FaPhone />
                  </IconWrapper>
                  <InfoContent>
                    <h4>Phone Number</h4>
                    <p>6203306234</p>
                  </InfoContent>
                </InfoItem>
                
                <InfoItem>
                  <IconWrapper>
                    <FaEnvelope />
                  </IconWrapper>
                  <InfoContent>
                    <h4>Email Address</h4>
                    <p>connect@datadigital.in</p>
                  </InfoContent>
                </InfoItem>
                
                <InfoItem>
                  <IconWrapper>
                    <FaClock />
                  </IconWrapper>
                  <InfoContent>
                    <h4>Business Hours</h4>
                    <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p>Saturday - Sunday: Closed</p>
                  </InfoContent>
                </InfoItem>
              </InfoItems>
              
              <SocialLinks>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaFacebookF />
                </SocialLink>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </SocialLink>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaLinkedinIn />
                </SocialLink>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </SocialLink>
              </SocialLinks>
            </InfoCard>
            
            <MapContainer>
              <iframe 
                src="https://www.google.com/maps?q=Saguna+More,+Patna,+Bihar&output=embed" 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              />
            </MapContainer>
          </ContactInfo>
          
          <ContactForm
            initial={{ opacity: 0, x: isReturnVisit ? 20 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ 
              type: 'spring', 
              stiffness: isReturnVisit ? 150 : 100, 
              damping: isReturnVisit ? 20 : 15, 
              delay: isReturnVisit ? 0 : 0.2 
            }}
          >
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15 }}
            >
              Send Us a Message
            </SectionTitle>
            
            <FormCard>
              <form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="name">Your Name</Label>
                    <Input type="text" id="name" placeholder="John Doe" required />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="email">Email Address</Label>
                    <Input type="email" id="email" placeholder="john@example.com" required />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input type="tel" id="phone" placeholder="6203306234" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="company">Company</Label>
                    <Input type="text" id="company" placeholder="Your Company" />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <Label htmlFor="subject">Subject</Label>
                  <Input type="text" id="subject" placeholder="How can we help you?" required />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="message">Your Message</Label>
                  <TextArea id="message" placeholder="Tell us about your project..." required />
                </FormGroup>
                
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <Button 
                    variant="accent" 
                    size="large" 
                    type="submit"
                    style={{
                      background: 'linear-gradient(135deg, #16D3A9 0%, #4D6DE3 100%)',
                      boxShadow: '0 10px 20px rgba(22, 211, 169, 0.2)',
                      border: 'none'
                    }}
                  >
                    Send Message
                  </Button>
                </motion.div>
              </form>
            </FormCard>
          </ContactForm>
        </ContactContainer>
      </Section>
    </>
  );
};

export default Contact;
